import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Quiz,
  People,
  TrendingUp,
  Assignment,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import statsAPI from '../../services/statsAPI';
import quizAPI from '../../services/quizAPI';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentQuizzes, setRecentQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('📊 Fetching dashboard stats...');
      
      // Récupérer les statistiques du dashboard
      const statsResponse = await statsAPI.getDashboardStats();
      console.log('✅ Stats received:', statsResponse);
      
      setStats(statsResponse.stats);
      
      // Récupérer les quiz récents pour la liste
      const quizzesResponse = await quizAPI.getAll({ limit: 5, sort: '-createdAt' });
      console.log('✅ Recent quizzes:', quizzesResponse);
      
      const quizzes = quizzesResponse.quizzes || quizzesResponse.data?.quizzes || [];
      setRecentQuizzes(quizzes.slice(0, 5));
      
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du chargement des données';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('❌ Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={`${color}.main`}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main`, opacity: 0.3 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard Admin
      </Typography>
      
      {/* Statistiques */}
      {stats && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Quiz Publiés"
                value={`${stats.quizzes.published}/${stats.quizzes.total}`}
                icon={<Quiz sx={{ fontSize: 40 }} />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Étudiants"
                value={`${stats.students.active}/${stats.students.total}`}
                subtitle="actifs/total"
                icon={<People sx={{ fontSize: 40 }} />}
                color="secondary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Tentatives"
                value={stats.attempts.completed}
                subtitle={`${stats.attempts.inProgress} en cours`}
                icon={<Assignment sx={{ fontSize: 40 }} />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Taux de Réussite"
                value={`${stats.performance.successRate}%`}
                subtitle={`Score moyen: ${stats.performance.averageScore}%`}
                icon={<CheckCircle sx={{ fontSize: 40 }} />}
                color="warning"
              />
            </Grid>
          </Grid>

          {/* Activité récente */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📈 Activité (7 derniers jours)
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {stats.attempts.recent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    tentatives récentes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ✅ Taux de Complétion
                  </Typography>
                  <Typography variant="h3" color="success.main">
                    {stats.performance.completionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.attempts.completed}/{stats.attempts.total} complétés
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Actions rapides */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Actions Rapides
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/quiz/create')}
              startIcon={<Quiz />}
            >
              Créer un Quiz
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/quiz')}
              startIcon={<Assignment />}
            >
              Gérer les Quiz
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/students')}
              startIcon={<People />}
            >
              Gérer les Étudiants
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Quiz récents */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quiz Récents
          </Typography>
          {recentQuizzes.length === 0 ? (
            <Typography color="text.secondary">
              Aucun quiz créé pour le moment.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentQuizzes.map((quiz) => (
                <Box
                  key={quiz._id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6">{quiz.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quiz.description}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={quiz.status}
                        color={quiz.status === 'published' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/quiz/${quiz._id}/edit`)}
                  >
                    Modifier
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminDashboard;

